import { ApolloProvider } from "@apollo/client";
import { Column, Spacing } from "@stenajs-webui/core";
import { LoadingScreen } from "@stenajs-webui/panels";
import { cssColor } from "@stenajs-webui/theme";
import * as React from "react";
import { useEffect } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { apolloClient, msalInstance } from "./apollo-client/ApolloClient";
import { fetchNegotiationYearsForProfile } from "@/redux/profile/thunks/FetchNegotiationYearsForProfile";
import { AuthMsalGate } from "./config/auth/AuthMsalGate";
import { UserProfileGate } from "./config/auth/UserProfileGate";
import { ErrorBoundary } from "./config/ErrorBoundary";
import { ProfileCurrencyErrorBoundary } from "./config/ProfileCurrencyErrorBoundary";
import { createReduxStore } from "@/redux/StoreFactory";
import { NegotiationYearGate } from "./contexts/NegotiationYearGate";
import { ErrorPage } from "./LoginPage";
import { NotificationCenter } from "@/features/notification-center/NotificationCenter";
import { NavBar } from "./features/nav-bar/NavBar";
import { FeatureFlagsProvider } from "./config/featureFlags/FeatureFlagsContext";
import { NotificationSubscriberWrapper } from "./features/notification-center/NotificationSubscriberWrapper";
import { Outlet } from "react-router";

const { persistor, store } = createReduxStore();

const removeDeprecatedKeysFromLocalStorage = () => {
  const keysDeleted: boolean = JSON.parse(
    localStorage.getItem("keysDeleted") || "false"
  );
  if (!keysDeleted) {
    Object.keys(localStorage).forEach((key) => {
      if (!key.includes("persist")) {
        localStorage.removeItem(key);
      }
    });
    localStorage.setItem("keysDeleted", JSON.stringify(true));
  }
};

export const App: React.FC = () => {
  const locale = navigator.language;

  useEffect(() => {
    fetchNegotiationYearsForProfile();
    removeDeprecatedKeysFromLocalStorage();
  }, []);

  return (
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <PersistGate loading={<LoadingScreen />} persistor={persistor}>
          <ErrorBoundary
            renderFallback={(error) => (
              <ErrorPage text={error.error?.message ?? ""} />
            )}
          >
            <AuthMsalGate msalInstance={msalInstance}>
              <UserProfileGate
                spinner={
                  <Spacing num={16}>
                    <LoadingScreen />
                  </Spacing>
                }
              >
                <FeatureFlagsProvider>
                  <NegotiationYearGate
                    spinner={
                      <Spacing num={16}>
                        <LoadingScreen />
                      </Spacing>
                    }
                  >
                    <NotificationSubscriberWrapper />
                    <Column
                      background={cssColor("--lhds-color-ui-200")}
                      minHeight={"100vh"}
                      className={"main-column"}
                    >
                      <NavBar msalInstance={msalInstance} />
                      <ProfileCurrencyErrorBoundary />
                      <ErrorBoundary
                        renderFallback={(error) => (
                          <ErrorPage text={error.error?.message ?? ""} />
                        )}
                      >
                        <Outlet />
                        <NotificationCenter />
                      </ErrorBoundary>
                    </Column>
                  </NegotiationYearGate>
                </FeatureFlagsProvider>
              </UserProfileGate>
            </AuthMsalGate>
          </ErrorBoundary>
        </PersistGate>
      </Provider>
    </ApolloProvider>
  );
};
